import { AlertTriangle, Loader2 } from "lucide-react";
import { Modal } from "./Modal";
import { cn } from "@/lib/utils";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
  loading?: boolean;
  onConfirm: () => void | Promise<void>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  variant = "default",
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  const isDanger = variant === "danger";
  const resolvedConfirmLabel =
    confirmLabel ?? (isDanger ? "Delete" : "Confirm");

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Modal open={open} onOpenChange={loading ? () => {} : onOpenChange}>
      <Modal.Content size="sm" hideCloseButton>
        <div className="p-6">
          <div className="flex items-start gap-3">
            {isDanger && (
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
              <div className="mt-2 text-sm text-slate-600">{message}</div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2",
                isDanger
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-amber-500 text-white hover:bg-amber-600"
              )}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {resolvedConfirmLabel}
            </button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}
